import { ArrowRightLeft, Coffee, Users, TrendingUp, BarChart3 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Queues as QueuesApi } from '@/lib/api';
import { useAuth } from '@/store/auth';
import { useBackendOnline } from '@/store/connection';
import { useAgentState } from '@/lib/agentState';
import { useActivityFeed } from '@/lib/useActivityFeed';
import { useTodayStats, fmtHHMMSS, type TodayStats } from '@/lib/useTodayStats';
import { useAgentDurations } from '@/lib/useAgentDurations';
import { useWeeklyTrend, type WeeklyTrend } from '@/lib/useWeeklyTrend';
import { CallDonut, DonutLegend } from '@/components/workspace/CallDonut';
import { Sparkline } from '@/components/workspace/Sparkline';
import { AIInsight } from '@/components/workspace/AIInsight';
import clsx from 'clsx';

/**
 * Çalışma Alanı — Mission Control.
 *
 * Üst yarı: Greeting + ActivityRings (hedef ilerlemesi) + Trend panel.
 * Orta: 4 küçük stat kart (Aktarım, Mola, Ort, Max).
 * Alt: Kuyruk durumu + Son aktivite (5-col grid).
 *
 * Tüm metrikler /api/activity-logs/me'den gerçek zamanlı çekiliyor —
 * useTodayStats (15s refetch) + useWeeklyTrend (60s refetch).
 */
export default function Workspace() {
  const { user } = useAuth();
  const agentState = useAgentState();
  const firstName = (user?.display_name ?? '').split(' ')[0] || user?.username || '';
  const stats = useTodayStats();
  const trend = useWeeklyTrend();

  return (
    <div className="px-5 py-3 max-w-[1300px] mx-auto">
      {/* Mission Control — greeting + donut + trend panel.
          Sayfa başlığı/breadcrumb KALDIRILDI — sidebar zaten aktif sayfayı
          gösteriyor; compact (kaydırmasız) görünüm için. Veri otomatik
          tazeleniyor (15-60s refetch) — manuel "Yenile" gerekmez. */}
      <section className="grid grid-cols-1 lg:grid-cols-5 gap-2.5 mb-2.5">
        <HeroPanel firstName={firstName} agentState={agentState} stats={stats} trend={trend} />
        <DonutPanel stats={stats} />
        <TrendPanel stats={stats} trend={trend} />
      </section>

      {/* Compact stat row */}
      <div className="grid grid-cols-3 gap-2.5 mb-2.5">
        <MicroStat icon={<ArrowRightLeft size={13} />} tone="brand"    label="Aktarım"      value={String(stats.transfers)} hint={stats.transfers === 0 ? 'henüz yok' : 'bugün toplam'} />
        <MicroStat icon={<Coffee size={13} />}         tone="warning"  label="Mola"          value={`${stats.breakCount} / 2`} hint="90 dk limit" />
        <MicroStat icon={<BarChart3 size={13} />}      tone="success"  label="Toplam görüşme" value={fmtHHMMSS(stats.totalTalkSec)} hint="bugün" mono />
      </div>

      {/* Two-column — kuyruk:aktivite ≈ 60:40 */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-2.5">
        <div className="lg:col-span-3">
          <QueueStatusCard />
        </div>
        <div className="lg:col-span-2">
          <ActivityFeedCard />
        </div>
      </div>
    </div>
  );
}

/* ─── Hero (greeting + insight + state-aware timer) ───────────── */

function HeroPanel({ firstName, agentState, stats, trend }: {
  firstName: string;
  agentState: ReturnType<typeof useAgentState>;
  stats: TodayStats;
  trend: WeeklyTrend;
}) {
  const durations = useAgentDurations();
  const hello = timeBasedGreeting();

  const STATE_VIBE: Record<typeof agentState, { headline: React.ReactNode; subtitle: string; bg: string; ring: string }> = {
    'kuyruk-disi': {
      headline: <>{hello}<br /><strong className="text-brand-600">{firstName}</strong> 👋</>,
      subtitle: 'kuyruğa girince akmaya başlar',
      bg: 'bg-gradient-to-br from-surface to-brand-50/40',
      ring: 'ring-brand-100',
    },
    'kuyrukta': {
      headline: <>{hello}<br /><strong className="text-emerald-700">{firstName}</strong> 👋</>,
      subtitle: 'çağrı bekliyorsun',
      bg: 'bg-gradient-to-br from-surface to-emerald-50/50',
      ring: 'ring-emerald-100',
    },
    'active': {
      headline: <><strong className="text-brand-700">Görüşmedesin</strong></>,
      subtitle: 'odakta kalmaya çalış',
      bg: 'bg-gradient-to-br from-surface to-orange-50/50',
      ring: 'ring-blue-100',
    },
    'mola': {
      headline: <><strong className="text-amber-700">Mola hakkın</strong> 🍵</>,
      subtitle: 'şarj olmaya devam',
      bg: 'bg-gradient-to-br from-surface to-amber-50/50',
      ring: 'ring-amber-100',
    },
  };
  const vibe = STATE_VIBE[agentState];

  return (
    <div className={clsx('lg:col-span-2 rounded-2xl ring-1 p-4 flex flex-col justify-between', vibe.bg, vibe.ring)}>
      <div>
        <div className="text-[17px] font-semibold tracking-tight text-slate-900 leading-tight">
          {vibe.headline}
        </div>
        <div className="text-[12px] text-slate-500 mt-0.5">{vibe.subtitle}</div>
      </div>
      <div className="mt-3 space-y-2.5">
        <AIInsight stats={stats} trend={trend} />

        {/* BUGÜNKÜ TOPLAMLAR — kümülatif (logout/mola sonrası kaldığı yerden).
            Bağlı = online (connect→disconnect). Mola = kuyruktan çıkış olduğu için
            Kuyrukta = çağrı alabilir (hazır) süre, MOLA HARİÇ. Mola ayrı sayaç.
            Anlık durum/sayaç topbar'da. */}
        <div className="grid grid-cols-2 gap-2">
          <DurationStat label="Bağlı" value={fmtHHMMSS(durations.onlineSec)} active={durations.online} stale={durations.stale} tone="sky" />
          <DurationStat label="Kuyrukta" value={fmtHHMMSS(durations.queueSec)} active={durations.inQueue} stale={durations.stale} tone="emerald" />
          <DurationStat label="Mola" value={fmtHHMMSS(durations.molaSec)} active={durations.onBreak} stale={durations.stale} tone="amber" />
          <DurationStat
            label="Boşta"
            value={durations.onCall ? 'Görüşmede' : fmtHHMMSS(durations.idleSec)}
            active={durations.inQueue && !durations.onCall}
            stale={durations.stale}
            tone="violet"
          />
        </div>
        {durations.stale && (
          <div className="mt-1.5 flex items-center gap-1.5 text-[10px] font-medium text-red-600">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse flex-shrink-0" />
            Bağlantı yok — sayaçlar donduruldu (son bilinen değer)
          </div>
        )}
      </div>
    </div>
  );
}

function DurationStat({ label, value, active, stale, tone }: {
  label: string; value: string; active: boolean; stale?: boolean; tone: 'brand' | 'emerald' | 'sky' | 'amber' | 'violet';
}) {
  const dotOn = { brand: 'bg-brand-500', emerald: 'bg-emerald-500', sky: 'bg-sky-500', amber: 'bg-amber-500', violet: 'bg-violet-500' }[tone];
  // Bağlantı koptuysa nabız atma — gri nokta + soluk değer (canlı değil sinyali).
  const live = active && !stale;
  const dot = live ? dotOn : 'bg-slate-300';
  return (
    <div className={clsx('rounded-xl bg-surface/60 ring-1 ring-slate-200/60 px-2.5 py-2', stale && 'opacity-60')}>
      <div className="flex items-center gap-1.5 mb-0.5">
        <span className={clsx('w-1.5 h-1.5 rounded-full flex-shrink-0', dot, live && 'animate-pulse')} />
        <span className="text-[9px] font-semibold uppercase tracking-wide text-slate-500 truncate">{label}</span>
      </div>
      <div className="text-[14px] font-bold font-mono tabular-nums text-slate-900 leading-none">{value}</div>
    </div>
  );
}

function timeBasedGreeting(): string {
  const h = new Date().getHours();
  if (h >= 5 && h < 11) return 'Günaydın';
  if (h >= 11 && h < 17) return 'İyi günler';
  if (h >= 17 && h < 22) return 'İyi akşamlar';
  return 'İyi geceler';
}

/* ─── Donut panel (3 segment: cevaplanan / cevapsız / giden) ──── */

function DonutPanel({ stats }: { stats: TodayStats }) {
  const answered = { label: 'Cevap',    value: stats.callsAnswered, colorClass: 'text-emerald-500' };
  const missed   = { label: 'Cevapsız', value: stats.callsMissed,   colorClass: 'text-red-500' };
  const outbound = { label: 'Giden',    value: stats.callsOutbound, colorClass: 'text-amber-500' };
  // Halkalar sadece gelen (cevap/cevapsız) — daha sade. Giden legend'de durur.
  return (
    <div className="lg:col-span-2 rounded-2xl bg-surface ring-1 ring-slate-200/70 p-3 flex flex-col items-center justify-center shadow-sm">
      <CallDonut segments={[answered, missed]} size={134} />
      <DonutLegend segments={[answered, missed, outbound]} />
    </div>
  );
}

/* ─── Trend panel (7-day sparkline + delta, no target) ────────── */

function TrendPanel({ stats, trend }: { stats: TodayStats; trend: WeeklyTrend }) {
  const todayCalls = stats.callsAnswered + stats.callsOutbound;
  const deltaPositive = trend.todayDeltaPct !== null && trend.todayDeltaPct >= 0;

  return (
    <div className="rounded-2xl bg-surface ring-1 ring-slate-200/70 p-3.5 shadow-sm flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-1.5 mb-2 text-[11px] font-semibold uppercase tracking-widest text-slate-500">
        <BarChart3 size={11} className="text-brand-500" />
        Bugünkü tempo
      </div>

      {/* Bugünkü toplam — büyük */}
      <div className="flex items-baseline gap-2 mb-1">
        <div className="text-[28px] font-bold text-slate-900 tabular-nums leading-none">{todayCalls}</div>
        <div className="text-[11.5px] text-slate-400">çağrı bugün</div>
      </div>

      {/* Delta */}
      {trend.todayDeltaPct !== null ? (
        <div className={clsx(
          'inline-flex items-center self-start text-[11px] font-semibold tabular-nums px-1.5 py-0.5 rounded',
          deltaPositive ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700',
        )}>
          {deltaPositive ? '+' : ''}{trend.todayDeltaPct}% son 6 günden
        </div>
      ) : (
        <div className="text-[10.5px] text-slate-400">karşılaştırma için geçmiş veri yok</div>
      )}

      {/* Sparkline (son 7 gün) */}
      <div className="mt-auto pt-2">
        <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Son 7 gün</div>
        <Sparkline data={trend.callsByDay} width={220} height={30} colorClass="text-brand-500" />
      </div>
    </div>
  );
}

/* ─── Compact stat row ────────────────────────────────────────── */

function MicroStat({ icon, tone, label, value, hint, mono }: {
  icon: React.ReactNode;
  tone: 'brand' | 'success' | 'warning' | 'neutral';
  label: string;
  value: React.ReactNode;
  hint: string;
  mono?: boolean;
}) {
  const iconCls = {
    brand:   'bg-brand-50 text-brand-600',
    success: 'bg-emerald-50 text-emerald-600',
    warning: 'bg-amber-50 text-amber-600',
    neutral: 'bg-slate-100 text-slate-600',
  }[tone];
  return (
    <div className="rounded-2xl bg-surface ring-1 ring-slate-200/70 p-3 shadow-sm flex items-center gap-2.5 hover:-translate-y-px hover:shadow-md transition">
      <div className={clsx('w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0', iconCls)}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] font-medium text-slate-500 uppercase tracking-widest">{label}</div>
        <div className={clsx('text-[16px] font-bold text-slate-900 tabular-nums leading-tight', mono && 'font-mono text-[14px]')}>
          {value}
        </div>
        <div className="text-[10px] text-slate-400 mt-0.5">{hint}</div>
      </div>
    </div>
  );
}

/* ─── Queue + Activity (alt iki sütun) ────────────────────────── */

function QueueStatusCard() {
  const backendOnline = useBackendOnline();
  const { data, isLoading } = useQuery({
    queryKey: ['my-queues'],
    queryFn: QueuesApi.mine,
    refetchInterval: 60_000,
    staleTime: 30_000,
    enabled: backendOnline,
  });
  return (
    <Card title="Üye olduğun kuyruklar" icon={<Users size={13} />}>
      {isLoading ? (
        <div className="py-6 text-center text-[12px] text-slate-400">Yükleniyor…</div>
      ) : !data || data.length === 0 ? (
        <div className="py-6 text-center">
          <Users size={20} className="text-slate-300 mx-auto mb-2" />
          <p className="text-[12.5px] text-slate-500 font-medium">Hiçbir kuyruğa üye değilsin</p>
          <p className="text-[11px] text-slate-400 mt-0.5">3CX yönetim panelinden eklenmen gerekiyor</p>
        </div>
      ) : (
        <div className="flex flex-col">
          {data.map((q) => (
            <div key={q.id} className="flex items-center gap-2.5 py-1.5 border-b border-slate-50 last:border-0">
              <span className="w-7 h-7 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center flex-shrink-0">
                <Users size={13} />
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-[12.5px] font-medium text-slate-900 truncate">{q.name}</div>
                <div className="text-[10.5px] text-slate-400 font-mono">{q.number}</div>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <span className={clsx('w-1.5 h-1.5 rounded-full', q.loggedInCount > 0 ? 'bg-emerald-500' : 'bg-slate-300')} />
                <span className="text-[11px] tabular-nums font-medium text-slate-700">{q.loggedInCount}</span>
                <span className="text-[10.5px] text-slate-400">aktif</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function ActivityFeedCard() {
  const { groups } = useActivityFeed('all', '', 60);
  const recent = groups.flatMap((g) => g.items).slice(0, 6);

  const pillCls = (kind: 'call' | 'queue' | 'status' | 'transfer', label: string): string => {
    if (kind === 'call') {
      if (label === 'Kaçan' || label === 'Reddedildi') return 'bg-red-50 text-red-700';
      if (label === 'Giden') return 'bg-amber-50 text-amber-700';
      return 'bg-emerald-50 text-emerald-700';
    }
    if (kind === 'queue') return 'bg-emerald-50 text-emerald-700';
    if (kind === 'transfer') return 'bg-blue-50 text-blue-700';
    return 'bg-slate-50 text-slate-600';
  };
  const dotCls = (kind: 'call' | 'queue' | 'status' | 'transfer', label: string): string => {
    if (kind === 'call' && (label === 'Kaçan' || label === 'Reddedildi')) return 'bg-red-500';
    if (kind === 'call' && label === 'Giden') return 'bg-amber-500';
    if (kind === 'call') return 'bg-emerald-500';
    if (kind === 'queue') return 'bg-emerald-500';
    if (kind === 'transfer') return 'bg-blue-500';
    return 'bg-slate-400';
  };
  const fmtTime = (ts: number) => {
    const d = new Date(ts);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };
  const fmtDur = (sec?: number) => {
    if (!sec || sec <= 0) return '—';
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return m > 0 ? `${m}:${String(s).padStart(2, '0')}` : `${s}s`;
  };

  return (
    <Card title="Son aktivite" icon={<TrendingUp size={13} />} linkLabel="Tümü →" linkTo="/activity">
      {recent.length === 0 ? (
        <div className="py-6 text-center text-[11.5px] text-slate-400">Henüz aktivite yok</div>
      ) : (
        <div className="flex flex-col">
          {recent.map((e) => (
            <div key={e.key} className="flex items-center gap-2 py-1 text-[11.5px] min-w-0">
              <span className={clsx('w-2 h-2 rounded-full flex-shrink-0', dotCls(e.kind, e.label))} />
              <span className="text-[10.5px] text-slate-400 font-mono tabular-nums flex-shrink-0 w-9">{fmtTime(e.startedAt)}</span>
              <span className={clsx('inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap flex-shrink-0', pillCls(e.kind, e.label))}>{e.label}</span>
              <span className="font-mono text-[11px] text-slate-700 tabular-nums truncate flex-1 min-w-0">{e.primary}</span>
              <span className="text-[10.5px] text-slate-400 font-mono tabular-nums flex-shrink-0">{fmtDur(e.durationSec)}</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function Card({ title, icon, linkLabel, linkTo, children }: {
  title: string;
  icon: React.ReactNode;
  linkLabel?: string;
  linkTo?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="bg-surface border border-slate-100 rounded-2xl shadow-sm overflow-hidden h-full">
      <header className="flex items-center justify-between px-3.5 py-2 border-b border-slate-100">
        <div className="flex items-center gap-2 text-[12.5px] font-medium text-slate-900">
          <span className="w-[20px] h-[20px] rounded-md bg-brand-100 text-brand-700 flex items-center justify-center">{icon}</span>
          {title}
        </div>
        {linkLabel && linkTo && (
          // Client-side route (react-router) — <a href> KULLANMA: full page reload
          // tüm app'i remount eder, softphone re-register olur. <Link> ile app
          // mount kalır, softphone register'dan düşmez.
          <Link to={linkTo} className="text-[11px] text-brand-700 hover:text-brand-500 font-medium">{linkLabel}</Link>
        )}
      </header>
      <div className="p-3">{children}</div>
    </section>
  );
}
